'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { featurePages } from '@/lib/feature-pages';
import { PAGE_HERO_IMAGES } from '@/lib/marketing-images';

const accentColors: Record<string, string> = {
  cyan: 'from-neon-cyan to-neon-blue',
  blue: 'from-neon-blue to-neon-purple',
  purple: 'from-neon-purple to-neon-magenta',
  amber: 'from-neon-amber to-neon-magenta',
  rose: 'from-neon-magenta to-neon-purple',
  orange: 'from-neon-amber to-neon-magenta',
  red: 'from-neon-magenta to-neon-purple',
  slate: 'from-neon-cyan to-neon-blue',
  violet: 'from-neon-purple to-neon-blue',
  indigo: 'from-neon-purple to-neon-cyan',
  sky: 'from-neon-cyan to-neon-teal',
  green: 'from-neon-teal to-neon-cyan',
};

export default function FeaturesOverview() {
  return (
    <>
      <section className="relative overflow-hidden gradient-hero-vivid grid-bg py-24 lg:py-32">
        <div className="absolute top-1/3 left-1/4 h-[400px] w-[400px] rounded-full gradient-orb-purple opacity-30 blur-[80px]" />
        <div className="absolute bottom-0 right-1/4 h-[300px] w-[300px] rounded-full gradient-orb-cyan opacity-20 blur-[60px]" />

        <div className="relative z-10 mx-auto max-w-7xl px-6 text-center lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-cyan mb-4">
            <Sparkles className="h-3 w-3" />
            Platform Features
          </span>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Everything your fleet needs,{' '}
            <span className="text-gradient">built in.</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-400">
            From live GPS tracking to AI-powered insights, GlonexTrack provides a complete telematics
            intelligence platform for modern fleet operations.
          </p>
          <div className="mx-auto mt-8 max-w-4xl">
            <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
              <Image
                src={PAGE_HERO_IMAGES.features.src}
                alt={PAGE_HERO_IMAGES.features.alt}
                width={1600}
                height={900}
                className="h-auto w-full rounded-2xl object-cover"
                priority
              />
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-20 lg:py-28 gradient-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featurePages.map((feature, i) => {
              const grad = accentColors[feature.color] || 'from-neon-cyan to-neon-purple';
              return (
                <motion.div
                  key={feature.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                >
                  <Link href={`/features/${feature.slug}`} className="group block h-full">
                    <div className="glass-card-vivid flex h-full flex-col rounded-2xl p-8">
                      <div className={`mb-4 inline-flex self-start rounded-xl bg-gradient-to-br ${grad} p-3 text-white shadow-lg`}>
                        <feature.icon className="h-6 w-6" />
                      </div>
                      <h3 className="mb-1 text-lg font-semibold text-white">{feature.title}</h3>
                      <p className="mb-1 text-xs font-medium uppercase tracking-wider text-neon-cyan/60">
                        {feature.tagline}
                      </p>
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-300">
                        {feature.description.slice(0, 150)}...
                      </p>
                      <div className="mt-4 flex items-center gap-1 text-sm font-semibold text-neon-cyan opacity-0 transition-all group-hover:opacity-100 group-hover:gap-2">
                        Explore <ArrowRight className="h-4 w-4" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
